import {useEffect, useState} from "react";
import {KatButton, KatModal} from "@amzn/katal-react";
import * as React from "react";
import {EventEmitter} from "events";

interface ConfirmationDialogProps {
    openDialogEvent: string;
    closeDialogEvent: string;
    content: string;
    eventBus: EventEmitter;
}

const ConfirmationDialog = (props: ConfirmationDialogProps) => {
    const [isOpen, setIsOpen] = useState(false);
    /**
     * Incremented on every open request and used as the modal's key, so each request mounts a fresh
     * element. See the comment on KatModal below for why keying is needed on top of onClose.
     */
    const [openCount, setOpenCount] = useState(0);
    const {eventBus} = props;

    useEffect(() => {
        const handleOpenDialog = (isOpen: any) => {
            if (isOpen) {
                setIsOpen(true);
                // Always a state change, even when isOpen is already true, so opening never depends
                // on React observing a false -> true transition on `isOpen`.
                setOpenCount(count => count + 1);
            }
        }

        eventBus.on(props.openDialogEvent, handleOpenDialog);
        return () => {
            eventBus.removeListener(props.openDialogEvent, handleOpenDialog);
        };
    }, []);

    const onConfirmationCancel = () => {
        setIsOpen(false);
    }

    const onConfirmationSubmit = async () => {
        setIsOpen(false);
        eventBus.emit(props.closeDialogEvent, true);
    }

    return (
        /*
         * Two defences against the same failure: the dialog could not be reopened after being
         * dismissed by anything other than Cancel.
         *
         * 1. onClose. kat-modal's own close() -- reached by the header X, a click outside the dialog,
         *    and Escape -- sets visible=false on the element itself and emits `close`. With no
         *    listener, React never learned the dialog had gone and `isOpen` stayed true while the
         *    element was hidden. The next open request then called setIsOpen(true) with true already
         *    set: no state change, no re-render, `visible` never rewritten, dialog never reappeared.
         *    Cancel was unaffected because it drives the React state directly.
         *
         * 2. key={openCount}. Even with onClose wired, visibility has two owners -- this prop and the
         *    element mutating itself -- so a close event that fails to arrive would strand it again.
         *    Keying on the open counter remounts a fresh element per open, which cannot carry over a
         *    stale hidden state.
         */
        <KatModal key={openCount} visible={isOpen} onClose={onConfirmationCancel} data-testid='kat-modal'>
            <span slot="title">Confirmation</span>
            <p>{props.content}</p>
            <div slot="footer" className="kat-group-horizontal">
                <KatButton variant={"secondary"} label={"Cancel"}
                           onClick={onConfirmationCancel} data-testid='cancel-btn'></KatButton>
                <KatButton label={"Submit"} onClick={onConfirmationSubmit} data-testid='submit-btn'></KatButton>
            </div>
        </KatModal>
    );
};

export default ConfirmationDialog;